import type { Account } from './account';
import type { AccountFees } from './accountFees';
import type { DspRestriction, Feature_Flags_Enum, User } from './gqlTypes';
import type { ConstValues } from './helpers';
import type { Provider } from './provider';
import type { BaseRow } from './tableHelpers';

export const organizationType = {
  TechPartner: 'Tech Partner',
  Buyer: 'Buyer',
  Publisher: 'Publisher',
  BuyerDev: 'OpenX - Buyer Dev',
} as const;

export type OrganizationType = ConstValues<typeof organizationType>;

export const dspRestrictionModes = {
  ALLOW: 'ALLOW',
  BLOCK: 'BLOCK',
} as const;

export type DspRestrictionModes = ConstValues<typeof dspRestrictionModes>;

export type OrganizationAllowedProvider = Pick<Provider, 'id' | 'name' | 'allowed_export_types'>;
export type OrganizationUserWithAccess = Pick<User, 'id' | 'first_name' | 'last_name' | 'email_address' | 'role'>;

export type OrganizationData = Pick<Account, 'id' | 'name' | 'organization_type'> & {
  allowed_countries?: string[];
  allowed_domains?: string[];
  allowed_providers?: {
    provider: OrganizationAllowedProvider;
  }[];
  description?: string | null;
  organization_type?: OrganizationType | null;
  exchange_account_partner_id?: string | null;
  feature_flags: { value: Feature_Flags_Enum; enabled?: boolean }[];
  parent_account?: Pick<Account, 'id' | 'name'> | null;
  exchange_account_network?: Pick<Account, 'id' | 'uid'> | null;
  exchange_account_network_id?: string | null;
  dsp_restriction?: DspRestriction[] | null;
  dsp_restriction_mode?: DspRestrictionModes | null;
  retargeting_enabled?: boolean | null;
  path_to_root?: string[];
  sub_accounts?: Pick<Account, 'id' | 'name'>[] | null;
} & Partial<AccountFees>;

export type OrganizationOptionData = Omit<OrganizationData, 'feature_flags' | 'organization_type'> & {
  feature_flags?: OrganizationData['feature_flags'];
  organization_type?: string | null;
};

export type OrganizationsList = Pick<Account, 'id' | 'name' | 'created_date'> & {
  allowed_countries?: string[];
  feature_flags?: { value: Feature_Flags_Enum }[];
  organization_type: string | null;
  parent_account?: Pick<Account, 'id' | 'name'> | null;
  path_to_root?: string[];
  sub_accounts?: OrganizationsList[] | null;
};

export type OrganizationApiKeyData = {
  first_name: string;
  expires_at?: string | null;
  created_date: string;
  id: string;
};

export type OrganizationPixelMetricData = BaseRow & {
  event_type: string;
  event_type_label: string;
  fires_30d?: number | null;
  unique_30d?: number | null;
  last_fire_at?: string | null;
};

export type OrganizationTreeRowData = OrganizationsList & {
  depth: number;
  expanded: boolean;
  children: OrganizationTreeRowData[];
};
